import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "member" ? "/portal" : "/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FBF9F5]">
      <div className="relative hidden lg:flex flex-col items-center justify-center bg-black p-12">
        <img src="/whf-logo.jpg" alt="World Harvest Fellowship - Auckland" className="w-full max-w-md object-contain" />
        <p className="mt-10 max-w-sm text-center font-serif text-xl leading-relaxed text-stone-400">
          “Shepherding our congregation with care — every member, every gift, every gathering.”
        </p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <img src="/whf-logo.jpg" alt="World Harvest Fellowship - Auckland" className="lg:hidden mb-8 w-48 rounded-xl" />
          <p className="text-xs font-semibold uppercase tracking-widest text-[#2D5A27]">Welcome back</p>
          <h2 className="mt-2 font-serif text-3xl font-bold text-[#1C241B]">Sign in to your church</h2>
          <p className="mt-2 text-sm text-[#7A8A78]">Enter your credentials to access the dashboard.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email address</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@church.org"
                className="h-11 bg-white"
                required
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs font-medium text-[#2D5A27] hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-11 bg-white"
                required
              />
            </div>
            {error && (
              <p className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2" data-testid="login-error">
                {error}
              </p>
            )}
            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="h-11 w-full bg-[#2D5A27] hover:bg-[#23471F] text-white font-semibold"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
          <p className="mt-6 text-center text-sm text-[#7A8A78]">
            New here?{" "}
            <Link to="/register" data-testid="register-link" className="font-medium text-[#2D5A27] hover:underline">Create a member account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
